"use client";

import { useCallback, useMemo } from "react";
import { useConvexAuth, useMutation, useQuery } from "convex/react";
import { api } from "@convex/_generated/api";
import type { Id } from "@convex/_generated/dataModel";
import { useGuestCart, type GuestCartItem } from "./use-guest-cart";
import { isConvexId } from "@/lib/convex/validate-id";

export interface CartProductSnapshot {
  unitPriceCents: number;
  productTitle: string;
  productTypeSlug: string;
}

function toCartItemId(cartItemId: string): Id<"cartItems"> {
  if (!isConvexId<"cartItems">(cartItemId)) {
    throw new Error(`Invalid cart item id: ${cartItemId}`);
  }
  return cartItemId;
}

export function useCart() {
  const { isAuthenticated } = useConvexAuth();
  const guest = useGuestCart();
  const { getItem: guestGetItem, items: guestItems } = guest;

  const serverCount = useQuery(
    api.cart.queries.getCartItemCount,
    isAuthenticated ? {} : "skip",
  );
  const addToCart = useAddToCart(isAuthenticated, guest.addItem);
  const { updateQuantity, removeItem, clearAll } = useCartItemEdits(
    isAuthenticated,
    guest,
  );

  const getItemForProduct = useCallback(
    (productId: string): { inCart: boolean; quantity: number } => {
      if (!isAuthenticated) {
        const item = guestGetItem(productId);
        return item
          ? { inCart: true, quantity: item.quantity }
          : { inCart: false, quantity: 0 };
      }
      return { inCart: false, quantity: 0 };
    },
    [isAuthenticated, guestGetItem],
  );

  return {
    itemCount: isAuthenticated ? (serverCount ?? 0) : guest.itemCount,
    itemCountReady: !isAuthenticated || serverCount !== undefined,
    isGuest: !isAuthenticated,
    isAuthenticated,
    addToCart,
    updateQuantity,
    removeItem,
    clearAll,
    getItemForProduct,
    guestItems: useMemo(
      () => (isAuthenticated ? [] : guestItems),
      [isAuthenticated, guestItems],
    ),
  };
}

function useAddToCart(
  isAuthenticated: boolean,
  guestAddItem: ReturnType<typeof useGuestCart>["addItem"],
) {
  const addToCartMutation = useMutation(api.cart.mutations.addToCart);
  return useCallback(
    async (
      productId: Id<"products">,
      quantity: number,
      snapshot: CartProductSnapshot,
    ) => {
      if (isAuthenticated) {
        return addToCartMutation({ productId, quantity });
      }

      guestAddItem({
        productId,
        quantity,
        unitPriceCents: snapshot.unitPriceCents,
        productTitle: snapshot.productTitle,
        productTypeSlug: snapshot.productTypeSlug,
      });
      return { cartItemId: productId, status: "added" as const };
    },
    [isAuthenticated, addToCartMutation, guestAddItem],
  );
}

function useCartItemEdits(
  isAuthenticated: boolean,
  guest: ReturnType<typeof useGuestCart>,
) {
  const {
    updateQuantity: guestUpdateQty,
    removeItem: guestRemoveItem,
    clearAll: guestClearAll,
  } = guest;
  const updateQtyMutation = useMutation(
    api.cart.mutations.updateCartItemQuantity,
  );
  const removeFromCartMutation = useMutation(api.cart.mutations.removeFromCart);
  const clearCartMutation = useMutation(api.cart.mutations.clearCart);

  const updateQuantity = useCallback(
    async (productIdOrCartItemId: string, quantity: number) => {
      if (!isAuthenticated) return guestUpdateQty(productIdOrCartItemId, quantity);
      return updateQtyMutation({
        cartItemId: toCartItemId(productIdOrCartItemId),
        quantity,
      });
    },
    [isAuthenticated, updateQtyMutation, guestUpdateQty],
  );

  const removeItem = useCallback(
    async (productIdOrCartItemId: string) => {
      if (!isAuthenticated) return guestRemoveItem(productIdOrCartItemId);
      return removeFromCartMutation({
        cartItemId: toCartItemId(productIdOrCartItemId),
      });
    },
    [isAuthenticated, removeFromCartMutation, guestRemoveItem],
  );

  const clearAll = useCallback(async () => {
    if (!isAuthenticated) return guestClearAll();
    return clearCartMutation({});
  }, [isAuthenticated, clearCartMutation, guestClearAll]);

  return { updateQuantity, removeItem, clearAll };
}

export type { GuestCartItem };
